import { nanoid } from '@lighthouse/tools'

import type {
    ActorEvent,
    CollectEvent,
    CursorProtocol,
    FieldCellValue,
    FilterByConditions,
    InnerTypeADTValue,
    Sorter,
    TypeInstanceMap
} from '../protocols'
import {
    type ContainerBreakPointConfigProtocol,
    DIRECTION,
    FLEX_ALIGN,
    type PartialExcludesIdAndName,
    type RichTextContentProtocol,
    type VariableADTvalue
} from '../protocols'

/** 分享设置 */
export type PageShareConfig = {
    titleV2?: RichTextContentProtocol
    descriptionV2?: RichTextContentProtocol
    image?: VariableADTvalue
    keywords?: string[]
}

/** 运行时分享覆盖配置 */
export type PageShareRuntimeConfig = {
    pageId: string
    title: string | undefined
    description: string | undefined
    image: string | undefined
    link: string | undefined
}

export enum PageOpenType {
    login,
    all,
    part
}

export type ArgumentsType = Extract<TypeInstanceMap, 'DATE' | 'TEXT' | 'NUMBER' | 'ARRAY' | 'BOOL'>

export interface ArgumentsSchema {
    id: string
    name: string
    desc?: string
    valuePath?: string
    innerType: ArgumentsType
}

export interface StateSchema extends ArgumentsSchema {
    defaultValue?: RichTextContentProtocol
}

export interface PageVariableSchema {
    id: string
    name: string
    dsId: string
    sorts: Sorter[]
    filter: FilterByConditions
    items: {
        id: string
        name: string
        fieldId: string
    }[]
}

export interface PageParameterAndVariable {
    pathParameter?: ArgumentsSchema[]
    queryParameter?: ArgumentsSchema[]
    variables?: PageVariableSchema[]
    states?: StateSchema[]
}

export interface PageDataVariableWidthRecord {
    id: string
    dsId: string
    recordId: string
}

export interface RouteParameterWithValue {
    id: string
    value: string | number | boolean | string[]
}

export type PageStateWithValue = Pick<StateSchema, 'id'> & InnerTypeADTValue

export interface PageParameterAndVariableWidthRecord {
    pageDataVariable: PageDataVariableWidthRecord[]
    pathParameter: RouteParameterWithValue[]
    queryParameter: RouteParameterWithValue[]
    pageState: PageStateWithValue[]
}

type StackId = string
type VariableId = string

export type RecordDependIds = {
    appId: string
    envId: string
    dsId: string
    recordId: string
}
/** 页面变量-记录数据（按 stackId 隔离，同一页面不同栈实例持有独立变量） */
export type PageVariableMappingRecord = Record<StackId, Record<VariableId, RecordDependIds>>

export type VariableItemId = string

/** 页面变量-输出数据 */
export type PageVariableMappingValue = Record<VariableId, Record<VariableItemId, FieldCellValue>>

/**
 * 页面类型
 * @export
 * @abstract
 * @class PageAbstract
 * @implements {UserGroupProtocols}
 */
export abstract class PageAbstract {
    id = nanoid(32)

    name = ''

    /**
     * @description  0 登录可见  1公开  2指定角色或部门
     */
    open = PageOpenType.all

    isHome = false

    roleIds: string[] = []

    departmentIds: string[] = []

    /** 分享设置 */
    shareConfig?: PageShareConfig

    /** 页面是否可访问 */
    isAccess?: boolean

    language = ''

    /** 自定义路由 */
    route?: string

    /** 导航栏可见 */
    isShowNavbar = true

    /** 导航栏可见 */
    isShowTabBar = true

    collectEvents?: CollectEvent[] = []

    cursor?: CursorProtocol

    // actions?: ScrollActionProtocol
    eventList?: ActorEvent[]

    breakPoint: ContainerBreakPointConfigProtocol = {
        id: 'desktop',
        visibility: {},
        size: {
            width: {
                size: 'fill'
            },
            height: {
                size: 'auto'
            },
            maxWidth: {
                size: 1200,
                unit: 'px'
            },
            minHeight: {
                size: 100,
                unit: '%'
            },
            overflow: 'auto'
        },
        layout: {
            align: {
                direction: DIRECTION.vertical,
                alignX: FLEX_ALIGN['flex-start'],
                alignY: FLEX_ALIGN['flex-start']
            },
            gap: {
                size: 8,
                unit: 'px'
            },
            padding: { size: [24, 24, 24, 24], unit: 'px' }
        },
        design: {}
    }

    breakPoints: PartialExcludesIdAndName<ContainerBreakPointConfigProtocol>[] = [
        {
            id: 'mobile'
        }
    ]

    abstract parameterAndVariable?: PageParameterAndVariable

    /**
     * 是否启用页面缓存
     * @default false
     */
    isKeepAlive?: boolean
}

export type PageMode = 'GROUP' | 'COMMON'

export type BasePage = PageAbstract & { type: 'COMMON'; path?: string; level?: number; parentId?: string }

export type GroupPage = {
    id: string
    name: string
    type: 'GROUP'
    language: string
    path?: string
    level?: number
    parentId?: string
    children: (GroupPage | BasePage)[]
}

export type PageItem = BasePage | GroupPage
